import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO } from "@/lib/format";
import { AlertTriangle, Info, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Notif yang ditutup disimpan per sesi login (dibersihkan saat login/logout di AuthContext)
export const DISMISS_KEY = "khn_dismissed_reminders";
const readDismissed = () => { try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || "[]"); } catch { return []; } };
const keyOf = (r) => r.id || `${r.title}|${r.due_date}`;

/** Kartu pengingat pajak (dipakai di dashboard semua jenis workspace). */
export default function PajakReminderList() {
  const [items, setItems] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ tanggal_bayar: todayISO(), nominal: 0, ntpn: "", keterangan: "" });
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/dashboard/pajak-reminder").then((r) => setItems(r.data?.reminders || [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const openPay = (r) => { setPayFor(r); setPay({ tanggal_bayar: todayISO(), nominal: Number(r.amount || 0), ntpn: "", keterangan: "" }); };
  const savePay = async () => {
    if (!pay.tanggal_bayar) { toast.error("Tanggal bayar wajib diisi"); return; }
    setSaving(true);
    try {
      await api.post("/tax-payments", {
        reminder_id: keyOf(payFor), jenis: (payFor.id || "").split("-")[0], periode: payFor.period || "",
        judul: payFor.title || "", tanggal_bayar: pay.tanggal_bayar, nominal: Number(pay.nominal || 0),
        ntpn: pay.ntpn || "", keterangan: pay.keterangan || "",
      });
      toast.success("Ditandai sudah bayar. Pengingat ini tidak akan muncul lagi.");
      setPayFor(null); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  const close = (r) => {
    const next = Array.from(new Set([...dismissed, keyOf(r)]));
    setDismissed(next);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
  };
  const visible = items.filter((r) => !dismissed.includes(keyOf(r)));
  if (!visible.length && !payFor) return null;
  return (
    <>
    <div className="space-y-3" data-testid="pajak-reminders">
      {visible.map((r, i) => (
        <div key={keyOf(r)} data-testid={`pajak-reminder-${i}`} data-reminder-id={keyOf(r)}
          className={`relative card-flat p-4 md:p-5 flex gap-4 border-l-4 ${r.level === "warning" ? "border-l-amber-500" : "border-l-blue-500"}`}>
          {r.level === "warning" ? <AlertTriangle className="text-amber-500 shrink-0" size={20} /> : <Info className="brand-text shrink-0" size={20} />}
          <div className="flex-1 min-w-0 pr-6">
            <div className="font-semibold text-slate-900 text-sm md:text-base">{r.title}</div>
            <div className="text-xs md:text-sm text-slate-600 mt-1">{r.message}</div>
            <div className="mt-2 flex flex-wrap items-center gap-3 md:gap-4 text-xs md:text-sm">
              <span className="text-slate-500">{r.amount_label || "Estimasi PPh 0,5%"}:</span>
              <span className="font-mono font-semibold brand-text">{rupiah(r.amount)}</span>
              <span className="text-slate-500">Jatuh tempo: <b className="text-slate-900">{tanggalID(r.due_date)}</b></span>
            </div>
            <div className="mt-3">
              <Button size="sm" variant="outline" onClick={() => openPay(r)} className="rounded-sm h-8 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid={`pajak-reminder-paid-${i}`}>
                <CheckCircle2 size={14} className="mr-1" /> Tandai Sudah Bayar
              </Button>
            </div>
          </div>
          <button type="button" onClick={() => close(r)} aria-label="Tutup notifikasi" title="Tutup"
            className="absolute top-2 right-2 p-1.5 rounded-sm text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            data-testid={`pajak-reminder-close-${i}`}>
            <X size={16} />
          </button>
        </div>
      ))}
    </div>
    <Dialog open={!!payFor} onOpenChange={(v) => !v && setPayFor(null)}>
      <DialogContent data-testid="tax-pay-dialog">
        <DialogHeader><DialogTitle>Tandai Sudah Bayar</DialogTitle></DialogHeader>
        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-sm p-3">{payFor?.title}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Tanggal Bayar <span className="text-rose-500">*</span></Label><Input type="date" value={pay.tanggal_bayar} onChange={(e) => setPay({ ...pay, tanggal_bayar: e.target.value })} data-testid="tax-pay-tanggal" /></div>
          <div className="space-y-1.5"><Label>Nominal Dibayar (Rp)</Label><CurrencyInput value={pay.nominal} onValueChange={(v) => setPay({ ...pay, nominal: v })} data-testid="tax-pay-nominal" /></div>
          <div className="md:col-span-2 space-y-1.5"><Label>NTPN / Kode Billing (opsional)</Label><Input value={pay.ntpn} onChange={(e) => setPay({ ...pay, ntpn: e.target.value })} placeholder="mis. 0123456789ABCDEF" data-testid="tax-pay-ntpn" /></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan (opsional)</Label><Textarea value={pay.keterangan} onChange={(e) => setPay({ ...pay, keterangan: e.target.value })} data-testid="tax-pay-ket" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setPayFor(null)} className="rounded-sm">Batal</Button>
          <Button onClick={savePay} disabled={saving} className="brand-btn rounded-sm" data-testid="tax-pay-save">{saving ? "Menyimpan..." : "Simpan"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
